/**
 * SupplierBrowseLayer — context-preserving slide-up bridge between the
 * guest experience and the marketplace. NOT a hard redirect; the guest
 * page stays mounted behind a slight backdrop blur while the supplier
 * layer rises from the bottom. The goal is continuity of emotion:
 *
 *     "I didn't navigate — I started planning my own version of this"
 *
 * URL: appends `?browse=1` so back-button closes the sheet instead of
 * leaving the event. Deep-link recovery also works — arriving with
 * ?browse=1 already set opens the sheet on mount.
 *
 * Pre-fills the search with the event's postcode + date so the guest
 * never sees a blank state. Category picker for the first tap.
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { X, MapPin, CaretRight, Sparkle } from '@phosphor-icons/react';
import { trackGrowth } from '../lib/growthTrack';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_CHIPS = [
  { id: 'photographer',    label: 'Photographer',    emoji: '📷' },
  { id: 'videographer',    label: 'Videographer',    emoji: '🎥' },
  { id: 'dj',              label: 'DJ / Band',       emoji: '🎧' },
  { id: 'photobooth',      label: 'Photo Booth',     emoji: '📸' },
  { id: 'venue',           label: 'Venue',           emoji: '🏛️' },
  { id: 'caterer',         label: 'Caterer',         emoji: '🍽️' },
  { id: 'florist',         label: 'Florist',         emoji: '🌼' },
  { id: 'cake_maker',      label: 'Cake',            emoji: '🎂' },
  { id: 'hair_makeup',     label: 'Hair & Makeup',   emoji: '💅' },
  { id: 'wedding_planner', label: 'Planner',         emoji: '✨' },
];

export default function SupplierBrowseLayer({ isOpen, onClose, event }) {
  const [category, setCategory] = useState('photographer');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  // Identity-bridge copy fades out once results land so the transition
  // feels like "turning moments into your own event" rather than
  // "I hit a search page".
  const [bridgeVisible, setBridgeVisible] = useState(true);
  const abortRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const postcode = event?.event_postcode || 'SW1A1AA';
    const date = (event?.event_date || '').slice(0, 10) || undefined;
    setLoading(true);
    setBridgeVisible(true);
    const t = setTimeout(() => setBridgeVisible(false), 340);
    // Abort any prior request so rapid category switches don't
    // render stale results.
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    axios.get(`${API}/suppliers/search`, {
      params: { category, postcode, radius_miles: 40, limit: 10, ...(date ? { date } : {}) },
      signal: controller.signal,
    })
      .then((r) => setResults(Array.isArray(r.data) ? r.data : (r.data?.results || [])))
      .catch(() => { /* network / 400 — leave prior results */ })
      .finally(() => setLoading(false));
    return () => { clearTimeout(t); controller.abort(); };
  }, [isOpen, category, event?.event_postcode, event?.event_date]);

  if (!isOpen) return null;

  const cap = (n) => (n > 99 ? '99+' : n);
  const eventName = event?.event_name || 'this event';

  return (
    <div
      className="fixed inset-0 z-[130] flex items-end justify-center"
      data-testid="supplier-browse-layer"
    >
      {/* Backdrop — blur + dim keeps the event page visible beneath,
          reinforcing "continuation, not navigation". */}
      <div
        className="absolute inset-0 bg-black/40"
        style={{ backdropFilter: 'blur(2px)' }}
        onClick={onClose}
      />
      {/* Sheet */}
      <div
        className="relative w-full max-w-md h-[92vh] rounded-t-3xl overflow-hidden bg-[#15161B] border-t border-x border-white/10 flex flex-col"
        style={{ animation: 'sbl-slide-up 340ms cubic-bezier(0.22, 1, 0.36, 1)' }}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-white/[0.06] hover:bg-white/10 flex items-center justify-center text-white/60 hover:text-white"
          aria-label="Close"
          data-testid="supplier-browse-close"
        >
          <X size={12} weight="bold" />
        </button>

        {/* Header — carries event context forward so the guest feels
            this is the NEXT step of the same journey. */}
        <div className="px-5 pt-7 pb-4 border-b border-white/5">
          <p className="text-[11px] font-black tracking-[2.5px] uppercase text-[#C29D52]/85 mb-1.5">
            ✨ Planning something like {eventName}?
          </p>
          <h2 className="text-xl font-black text-white leading-tight">
            Find suppliers for your own event
          </h2>
          {event?.event_postcode && (
            <p className="flex items-center gap-1 text-xs text-white/50 mt-1.5">
              <MapPin size={12} weight="bold" />
              Near {event.event_postcode}
              {event?.event_date ? ` · ${event.event_date}` : ''}
            </p>
          )}
        </div>

        {/* Category chips */}
        <div className="px-5 py-3 overflow-x-auto scrollbar-hide border-b border-white/5">
          <div className="inline-flex gap-1.5 whitespace-nowrap">
            {CATEGORY_CHIPS.map((c) => {
              const active = category === c.id;
              return (
                <button
                  key={c.id}
                  onClick={() => setCategory(c.id)}
                  className={`px-3 py-1.5 rounded-full text-[11px] font-black transition-all ${active ? 'text-white' : 'text-white/60 hover:text-white/90 bg-white/[0.04]'}`}
                  style={active ? { background: 'linear-gradient(135deg, #C29D52, #B89762)' } : {}}
                  data-testid={`supplier-browse-chip-${c.id}`}
                >
                  {c.emoji} {c.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {bridgeVisible && (
            <div className="flex flex-col items-center justify-center text-center py-10" data-testid="supplier-browse-bridge">
              <Sparkle size={22} weight="fill" className="text-[#C29D52] mb-3 animate-pulse" />
              <p className="text-sm font-black text-white/80">Turn moments like this into your own event</p>
            </div>
          )}
          {!bridgeVisible && loading && (
            <div className="flex justify-center py-10" data-testid="supplier-browse-loading">
              <div className="w-6 h-6 rounded-full border-2 border-white/10 border-t-[#C29D52] animate-spin" />
            </div>
          )}
          {!bridgeVisible && !loading && results.length === 0 && (
            <div className="text-center py-10" data-testid="supplier-browse-empty">
              <p className="text-sm text-white/50">No suppliers match right now.</p>
              <p className="text-xs text-white/35 mt-1">Try a different category.</p>
            </div>
          )}
          {!bridgeVisible && !loading && results.length > 0 && (
            <div className="space-y-2.5" data-testid="supplier-browse-results">
              {results.slice(0, 10).map((s) => (
                <a
                  key={s.id || s.supplier_id}
                  href={`/suppliers/${s.id || s.supplier_id}`}
                  onClick={(ev) => {
                    // Continuity-of-context handoff: stash a friendly
                    // intro line keyed by supplier_id + a short TTL.
                    // ChatPage reads + seeds it on mount, then deletes
                    // — so unrelated future chats stay clean.
                    try {
                      const sid = s.id || s.supplier_id;
                      const eventName = event?.event_name || 'a recent event';
                      sessionStorage.setItem(`chat_intro_${sid}`, JSON.stringify({
                        text: `Hi! I'm planning something similar to ${eventName} — your work caught my eye.`,
                        ts: Date.now(),
                      }));
                    } catch {}
                    // Track the click for the supplier→guest revenue
                    // attribution chain. Fire-and-forget.
                    try { trackGrowth('supplier_browse_result_click', { supplier_id: s.id || s.supplier_id, event_id: event?.event_id }); } catch {}
                    void ev;
                  }}
                  className="block rounded-2xl p-3 border border-white/8 hover:border-white/20 bg-white/[0.02] hover:bg-white/[0.04] transition-all"
                  data-testid={`supplier-browse-result-${s.id || s.supplier_id}`}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className="w-11 h-11 rounded-xl flex-shrink-0 bg-cover bg-center"
                      style={{
                        backgroundImage: s.cover_photo_url ? `url(${s.cover_photo_url})` : 'linear-gradient(135deg, #B89762, #C29D52)',
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="text-sm font-black text-white truncate">{s.business_name || 'Supplier'}</h3>
                        <CaretRight size={12} weight="bold" className="text-white/40 flex-shrink-0" />
                      </div>
                      <p className="text-[11px] text-white/45 mt-0.5 truncate">
                        {s.location_label || s.postcode || ''}
                        {typeof s.avg_response_seconds === 'number' && s.avg_response_seconds < 3600 ? ' · replies fast' : ''}
                      </p>
                      {typeof s.base_price_gbp === 'number' && s.base_price_gbp > 0 && (
                        <p className="text-[11px] font-black text-[#C29D52] mt-0.5">From £{cap(Math.round(s.base_price_gbp))}</p>
                      )}
                    </div>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes sbl-slide-up {
          from { transform: translateY(100%); opacity: 0.6; }
          to   { transform: translateY(0);    opacity: 1;   }
        }
      `}</style>
    </div>
  );
}

// Small presentational CTA used by GuestLanding + GuestHome to trigger
// the layer. Kept beside the layer on purpose so both import from one
// place and the tracking fires consistently.
export function SoftMarketplaceCTA({ eventId, onOpen, testid = 'guest-marketplace-cta' }) {
  const handleClick = () => {
    trackGrowth('guest_to_marketplace_click', {
      event_id: eventId,
      source: 'guest_flow',
    });
    onOpen?.();
  };
  return (
    <div className="max-w-md mx-auto mb-6 text-center">
      <button
        onClick={handleClick}
        className="text-xs text-white/55 hover:text-white transition-colors"
        data-testid={testid}
      >
        ✨ Planning your own event?
        <span className="ml-1 underline font-black text-[#C29D52]/85/90 hover:text-[#C29D52]/85">
          Browse suppliers →
        </span>
      </button>
    </div>
  );
}
